import { useCallback } from 'react';

import {
  UseDeletePhoneNumberParams,
  UseDeletePhoneNumberReturnType,
} from './useDeletePhoneNumber.decl';

import { SUCCESS_FEEDBACK_MESSAGES } from '@constants/translation.constants';
import { useToast } from '@dashboard/hooks';
import { ClientException } from '@dashboard/library';
import {
  DeleteExternalNumberMutation,
  DeleteExternalNumberMutationVariables,
} from '@generated/DeleteExternalNumberMutation';
import { DELETE_EXTERNAL_NUMBER_MUTATION } from '@graphql/mutations/DeleteExternalNumberMutation';
import { getExternalErrorTranslationStrings } from '@helpers/errors.helpers';
import { useGraphMutation } from '@hooks/useMutation';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';

/**
 * Hook to delete a phone number given a phone number.
 * @param phoneNumber - phone number to be deleted
 * @returns an object holding a function responsible for deleting a phone number
 */
export function useDeletePhoneNumber({
  user,
  phoneNumber,
}: UseDeletePhoneNumberParams): UseDeletePhoneNumberReturnType {
  const { t } = useTranslation();
  const toast = useToast();
  const [mutate] = useGraphMutation<
    DeleteExternalNumberMutation,
    DeleteExternalNumberMutationVariables
  >(DELETE_EXTERNAL_NUMBER_MUTATION);

  const deletePhoneNumber = useCallback(async () => {
    try {
      await mutate({
        variables: {
          ID: phoneNumber.ID,
          agentID: user.ID,
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({ __typename: 'AgentPhoneNumber', ID: phoneNumber.ID }),
            fields: (_, { DELETE }) => DELETE,
          });
        },
        optimisticResponse: {
          deleteExternalNumber: {
            __typename: 'DeleteExternalNumberOutput',
            ID: phoneNumber.ID,
          },
        },
      });
      toast.show({
        variant: 'success',
        message: t(SUCCESS_FEEDBACK_MESSAGES.DELETION_SUCCESS, {
          name: parsePhoneNumber(`+${phoneNumber.phoneNumber}`).formatInternational(),
        }),
      });
    } catch (e) {
      const { error } = e as ClientException;
      const errors = getExternalErrorTranslationStrings(error);

      toast.show({
        variant: 'error',
        message: t(errors),
      });
    }
  }, [phoneNumber.ID, phoneNumber.phoneNumber, mutate, toast, t, user.ID]);

  return { deletePhoneNumber };
}
